import {
  SimpleRoleType,
  WorkspaceSimpleRoleType,
} from "src/clients/taktile-api";
import { useRoleOverride } from "src/hooks/useCapabilities/RoleOverrideStore";
import { isOrgRoleAtLeast } from "src/hooks/useCapabilities/roleChecks";
import {
  ANY_FOLDER,
  Capabilities,
  FlowFolderScope,
} from "src/hooks/useCapabilities/types";
import { useIsTaktileOrgUser } from "src/hooks/useIsTaktileOrgUser";
import { DashboardPageParams } from "src/router/urls";
import {
  useHasFwdRoleForOrg,
  useIsFWDOrAdmin,
  useIsTaktileAdmin,
  useUserRoles,
} from "src/store/AuthStore";
import { useSimpleRole } from "src/store/SimpleRoleProvider";
import {
  ALL_GRANULAR_ROLES,
  FOLDER_DEPENDENCY_KEY,
  FolderGranularRole,
  GranularRoleKey,
  getGranularRoleDependencyKey,
  NO_FOLDER_ARGUMENT,
  WILDCARD_ARGUMENT,
  WORKSPACE_ADMIN_ROLE,
} from "src/userManagement/userPermissions/permissionFeatures";
import { TokenRole } from "src/utils/decodeTokenScope";
import { useParamsDecode } from "src/utils/useParamsDecode";

type WorkspaceScope = {
  orgId: string | undefined;
  wsId: string | undefined;
};

const matchesArgument = (value: string | undefined, expected: string) =>
  value === expected || value === WILDCARD_ARGUMENT;

const isHeldInWorkspace = (role: TokenRole, { orgId, wsId }: WorkspaceScope) =>
  !!orgId &&
  !!wsId &&
  matchesArgument(role.arguments.org_id, orgId) &&
  matchesArgument(role.arguments.ws_id, wsId);

const isGranularRole = (roleName: string) =>
  !!getGranularRoleDependencyKey(roleName);

const isFolderGranularRole = (roleName: string) =>
  getGranularRoleDependencyKey(roleName) === FOLDER_DEPENDENCY_KEY;

export type GranularRoleGrants = {
  /** Workspace administration has no granular role of its own; ws_admin carries it. */
  isWorkspaceAdmin: boolean;
  workspaceRoles: Set<GranularRoleKey>;
  /** Flow roles are granted per folder, keyed by the folder argument of the grant. */
  folderRoles: Map<FolderGranularRole, Set<string>>;
};

/** Grants held across the whole workspace, so every folder is covered. */
const workspaceWideGrants = (roleNames: string[]): GranularRoleGrants => {
  const granularRoles = roleNames.filter(isGranularRole) as GranularRoleKey[];

  return {
    isWorkspaceAdmin: false,
    workspaceRoles: new Set(
      granularRoles.filter((role) => !isFolderGranularRole(role)),
    ),
    folderRoles: new Map(
      granularRoles
        .filter(isFolderGranularRole)
        .map((role) => [
          role as FolderGranularRole,
          new Set([WILDCARD_ARGUMENT]),
        ]),
    ),
  };
};

export const ALL_GRANULAR_ROLE_GRANTS: GranularRoleGrants = {
  ...workspaceWideGrants(ALL_GRANULAR_ROLES),
  isWorkspaceAdmin: true,
};

export const collectGranularRoleGrants = (
  roles: TokenRole[] | undefined,
  scope: WorkspaceScope,
): GranularRoleGrants => {
  const heldRoles = (roles ?? []).filter((role) =>
    isHeldInWorkspace(role, scope),
  );

  const workspaceRoles = new Set(
    heldRoles
      .filter(
        (role) => isGranularRole(role.name) && !isFolderGranularRole(role.name),
      )
      .map((role) => role.name as GranularRoleKey),
  );

  const folderRoles = heldRoles
    .filter(
      (role) =>
        isFolderGranularRole(role.name) && !!role.arguments.flow_folder_id,
    )
    .map(
      (role) =>
        [
          role.name as FolderGranularRole,
          role.arguments.flow_folder_id,
        ] as const,
    )
    .reduce<GranularRoleGrants["folderRoles"]>(
      (grants, [granularRole, folder]) =>
        grants.set(
          granularRole,
          new Set([...(grants.get(granularRole) ?? []), folder]),
        ),
      new Map(),
    );

  return {
    isWorkspaceAdmin: heldRoles.some(
      (role) => role.name === WORKSPACE_ADMIN_ROLE,
    ),
    workspaceRoles,
    folderRoles,
  };
};

const hasFolderRole = (
  folderRoles: GranularRoleGrants["folderRoles"],
  granularRole: FolderGranularRole,
  folder: FlowFolderScope,
): boolean => {
  const grantedFolders = folderRoles.get(granularRole);
  if (!grantedFolders) {
    return false;
  }
  if (folder === ANY_FOLDER) {
    return grantedFolders.size > 0;
  }
  if (grantedFolders.has(WILDCARD_ARGUMENT)) {
    return true;
  }

  return grantedFolders.has(folder ?? NO_FOLDER_ARGUMENT);
};

type GranularCapabilitiesInput = {
  grants: GranularRoleGrants;
  orgRole: Nullable<SimpleRoleType>;
  isFWDOrAdmin: boolean;
  isFWDOrAdminForOrg: boolean;
  isLoading: boolean;
};

export const buildGranularCapabilities = ({
  grants,
  orgRole,
  isFWDOrAdmin,
  isFWDOrAdminForOrg,
  isLoading,
}: GranularCapabilitiesInput): Capabilities => {
  const has = (granularRole: GranularRoleKey) =>
    grants.workspaceRoles.has(granularRole);

  const canViewLogic = (folder: FlowFolderScope) =>
    hasFolderRole(grants.folderRoles, "flow_viewer", folder);
  const canEditLogic = (folder: FlowFolderScope) =>
    hasFolderRole(grants.folderRoles, "flow_editor", folder);
  const canDeployLogic = (folder: FlowFolderScope) =>
    hasFolderRole(grants.folderRoles, "flow_deployer", folder);

  const canViewAgents = has("agent_viewer");

  const canViewConnections = has("connection_viewer");

  // flow_viewer carries sandbox decision history, per its definition in roles.yaml.
  const canViewHistory =
    has("dh_viewer_sandbox") ||
    has("dh_viewer_live") ||
    canViewLogic(ANY_FOLDER);

  const canReviewCases = has("case_reviewer");
  const canEditCases = has("case_editor");
  const canAdminCases = has("case_admin");
  const canUseFiles = has("file_user");

  const canViewData = has("data_viewer");
  const canEditData = has("data_editor");
  const canAdminData = has("data_admin");

  const { isWorkspaceAdmin } = grants;
  const isOrgAdmin = isOrgRoleAtLeast(SimpleRoleType.ADMIN, orgRole);

  return {
    role: orgRole,
    workspaceRole: isWorkspaceAdmin ? WorkspaceSimpleRoleType.ADMIN : null,
    isLoading,
    flowVersions: {
      canAccess: canViewLogic,
      canPublish: (flow) => canDeployLogic(flow.flow_folder_id),
      canArchive: canDeployLogic,
      canSetSandboxDefault: canEditLogic,
      canSetDefault: canDeployLogic,
      canCreate: canEditLogic,
      canEdit: canEditLogic,
      canDeleteDraft: canEditLogic,
    },
    datasets: {
      canAccess: canEditLogic(ANY_FOLDER),
      canDelete: canEditLogic(ANY_FOLDER),
    },
    decisionHistory: {
      canAccess: canViewHistory,
    },
    flows: {
      canAccess: canViewLogic,
      canCreate: canEditLogic,
      canEdit: canEditLogic,
      canDeleteUnpublished: canEditLogic,
      canDeletePublished: canDeployLogic,
    },
    apiKeys: {
      canAccess: isWorkspaceAdmin,
    },
    aiUsageLimits: {
      canAccess: isWorkspaceAdmin,
    },
    connections: {
      canAccess: canViewConnections,
    },
    usersPermissions: {
      canAccess: isOrgAdmin,
    },
    workspaces: {
      canCreate: isOrgAdmin,
      canEdit: isWorkspaceAdmin,
    },
    outgoingWebhooks: {
      canAccess: isWorkspaceAdmin,
    },
    manualReview: {
      canAccess: canReviewCases,
    },
    flowCharts: {
      canAccess: canEditLogic(ANY_FOLDER),
      canCreate: canEditLogic(ANY_FOLDER),
      canEdit: canEditLogic(ANY_FOLDER),
      canDelete: canEditLogic(ANY_FOLDER),
    },
    jobs: {
      canAccess: isWorkspaceAdmin,
      canCreate: isWorkspaceAdmin,
      canEdit: isWorkspaceAdmin,
      canDelete: isWorkspaceAdmin,
      canDeleteSource: isWorkspaceAdmin,
      canDeleteDestination: isWorkspaceAdmin,
      canToggleStatus: isWorkspaceAdmin,
      canRunSandbox: isWorkspaceAdmin,
      canRunLive: isWorkspaceAdmin,
      canChangeSchedule: isWorkspaceAdmin,
      canDuplicate: isWorkspaceAdmin,
    },
    flowFolders: {
      canCreate: isWorkspaceAdmin,
      canEdit: isWorkspaceAdmin,
      canDelete: isWorkspaceAdmin,
    },
    auditLogs: {
      canAccess: isOrgAdmin,
    },
    outcomes: {
      canAccess: isWorkspaceAdmin,
      canCreate: isWorkspaceAdmin,
      canEdit: isWorkspaceAdmin,
      canDelete: isWorkspaceAdmin,
      canClear: isWorkspaceAdmin,
    },
    featureCatalogue: {
      canAccess: canViewData,
      canCreate: canAdminData || isFWDOrAdmin,
      canEdit: canAdminData || isFWDOrAdmin,
      canDuplicate: canAdminData || isFWDOrAdmin,
    },
    featureQueriesCatalogue: {
      canAccess: canViewData,
      canCreate: canEditData,
      canEdit: canEditData,
      canDuplicate: canEditData,
      canActivate: canEditData,
      // Backfill is hidden in the UI while the action is broken (ATL-1494).
      canBackfill: false,
      canViewQueryDetails: canViewData,
      canCreateQuery: canEditData,
      canEditSandboxQuery: canEditData,
      canEditLiveQuery: canAdminData || isFWDOrAdmin,
      canPreviewLive: canEditData,
      canPreviewSandbox: canEditData,
      canChangeSandboxStatus: canEditData,
      canChangeLiveStatus: canAdminData,
      canDeleteDisabledQuery: canEditData,
      canArchive: canEditData,
    },
    organization: {
      canEditLogo: isOrgAdmin,
    },
    sensitiveFields: {
      canReveal: isWorkspaceAdmin,
      canEdit: isWorkspaceAdmin,
    },
    apiDocs: {
      canAccess: isWorkspaceAdmin,
    },
    caseTemplates: {
      canAccess: canReviewCases,
      canEdit: canEditCases,
      canCreateVersion: canAdminCases,
      canCreate: isFWDOrAdminForOrg || isOrgAdmin,
      canArchivePublished: canAdminCases,
    },
    caseQueues: {
      canEdit: canAdminCases,
      canCreate: canAdminCases,
    },
    cases: {
      canAccess: canReviewCases,
      canCreate: canReviewCases,
      canManage: canAdminCases,
      canAssign: canEditCases,
      canViewDecisionTrace: canEditCases,
      canEditBlocks: canReviewCases,
    },
    caseAnalytics: {
      canAccess: canEditCases,
    },
    caseAnalyticsDashboards: {
      canCreate: canAdminCases,
    },
    liveAnalytics: {
      canAccess: isWorkspaceAdmin,
    },
    // No granular role carries workspace_analytics_dashboard; only ws_admin
    // confers it in the granular cohort.
    workspaceInsights: {
      canAccess: isWorkspaceAdmin,
    },
    caseFiles: {
      canEdit: canUseFiles,
    },
    simulations: {
      canAccess: isWorkspaceAdmin,
    },
    notebooks: {
      canAccess: isWorkspaceAdmin,
    },
    caseHistory: {
      canSeeDetails: isWorkspaceAdmin,
    },
    agents: {
      canAccess: canViewAgents,
    },
    prebuiltAgents: {
      canAccessSystemPrompt: isFWDOrAdminForOrg,
      canWrite: isFWDOrAdminForOrg,
    },
    scimToken: {
      canAccess: isOrgAdmin,
    },
  };
};

export const useGranularCapabilities = (): Capabilities => {
  const { role, isLoading } = useSimpleRole();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const roles = useUserRoles();
  const actualIsFWDOrAdmin = useIsFWDOrAdmin();
  const isTaktileAdmin = useIsTaktileAdmin();
  const hasFwdForOrg = useHasFwdRoleForOrg(orgId);
  const isTaktileUser = useIsTaktileOrgUser();
  const { overrideWorkspaceRole, overrideFWDOrAdmin } = useRoleOverride();

  const isOverridden = isTaktileUser && overrideWorkspaceRole !== null;
  const actualIsFWDOrAdminForOrg = isTaktileAdmin || hasFwdForOrg;

  // Taktile staff reach the workspace through their own roles; an override
  // drops that, so it can never widen what it sees.
  const grants =
    actualIsFWDOrAdminForOrg && !isOverridden
      ? ALL_GRANULAR_ROLE_GRANTS
      : collectGranularRoleGrants(roles, { orgId, wsId });

  return buildGranularCapabilities({
    grants,
    orgRole: role,
    isFWDOrAdmin: isOverridden ? overrideFWDOrAdmin : actualIsFWDOrAdmin,
    isFWDOrAdminForOrg: isOverridden
      ? overrideFWDOrAdmin
      : actualIsFWDOrAdminForOrg,
    isLoading,
  });
};
